import React, { useState, useEffect, useRef } from "react";
import { MessageCircleQuestion, X, Send, Sparkles } from "lucide-react";
import { api, formatApiError, SITE } from "../lib/api";

const SUGGESTIONS = [
  { icon: "📌", text: "Quem é o Antônio Rosalvo?" },
  { icon: "📋", text: "Quais são as principais conquistas dele?" },
  { icon: "📍", text: "Como Rosalvo atua no meu bairro?" },
  { icon: "💬", text: "Como faço para falar diretamente com ele?" },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "Olá! 👋 Sou a assistente virtual oficial do site do vereador Antônio Rosalvo. Posso tirar suas dúvidas sobre:\n\n📜 Trajetória política e biografia\n🏛️ Projetos aprovados e leis\n📍 Ações no seu bairro\n📞 Como entrar em contato\n\nComo posso te ajudar?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("rosalvo_chat_sid") || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setErr("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/chat", { message: msg, session_id: sessionId || null });
      if (!sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem("rosalvo_chat_sid", data.session_id);
      }
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Floating launcher */}
      {!open && (
        <div className="fixed bottom-24 right-6 z-50 flex items-end gap-2" data-testid="chat-launcher">
          <div className="hidden sm:flex items-center gap-2 bg-[#003F88] text-white px-4 py-2.5 shadow-lg rounded-full">
            <span className="inline-flex w-2 h-2 rounded-full bg-[#FFC72C] animate-pulse" />
            <span className="text-xs font-medium tracking-wide uppercase">Tire suas dúvidas · 24h</span>
          </div>
          <button
            data-testid="chat-open-btn"
            aria-label="Abrir assistente virtual"
            onClick={() => setOpen(true)}
            className="relative w-14 h-14 rounded-full bg-[#003F88] text-white shadow-lg hover:bg-[#002A5C] hover:scale-105 transition-all flex items-center justify-center"
          >
            <MessageCircleQuestion size={26} />
            <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-[#FFC72C] rounded-full border-2 border-white" />
          </button>
        </div>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed inset-x-4 bottom-4 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[400px] z-50 bg-white border border-neutral-200 shadow-2xl flex flex-col overflow-hidden"
          style={{ maxHeight: "min(80vh, 640px)" }}
        >
          {/* Header */}
          <div className="bg-[#003F88] text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 inline-flex items-center justify-center bg-white/15 rounded-full">
                <Sparkles size={18} className="text-[#FFC72C]" />
              </span>
              <div className="leading-tight">
                <div className="font-serif-display text-lg">Tire suas dúvidas · 24h</div>
                <div className="text-[10px] uppercase tracking-widest flex items-center gap-1.5 text-white/85">
                  <span className="w-1.5 h-1.5 bg-[#16a34a] rounded-full inline-block" />
                  Online agora
                </div>
              </div>
            </div>
            <button
              data-testid="chat-close-btn"
              onClick={() => setOpen(false)}
              aria-label="Fechar chat"
              className="text-white/80 hover:text-white p-1"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAF9F6]"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`chat-msg-${i}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "bg-[#003F88] text-white"
                      : "bg-white border border-neutral-200 text-neutral-800"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-loading">
                <div className="bg-white border border-neutral-200 px-4 py-3 inline-flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-[#003F88] rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-[#003F88] rounded-full animate-bounce" style={{ animationDelay: "0.15s" }} />
                  <span className="w-1.5 h-1.5 bg-[#003F88] rounded-full animate-bounce" style={{ animationDelay: "0.3s" }} />
                </div>
              </div>
            )}
            {err && (
              <div className="text-xs text-red-700 bg-red-50 border-l-2 border-red-500 px-3 py-2">
                {err}
              </div>
            )}

            {/* Suggestions appear only on first message */}
            {messages.length === 1 && !loading && (
              <div className="pt-2 space-y-2" data-testid="chat-suggestions">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s.text)}
                    data-testid={`chat-suggestion-${i}`}
                    className="w-full text-left text-sm bg-white border border-neutral-200 px-4 py-2.5 hover:border-[#003F88] hover:text-[#003F88] transition-colors"
                  >
                    <span className="mr-2">{s.icon}</span>
                    {s.text}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-neutral-200 bg-white p-3 flex items-center gap-2"
          >
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Digite sua dúvida..."
              className="flex-1 border border-neutral-300 px-3 py-2.5 text-sm focus:border-[#003F88] outline-none"
              disabled={loading}
            />
            <button
              type="submit"
              data-testid="chat-send-btn"
              disabled={loading || !input.trim()}
              className="w-10 h-10 bg-[#003F88] text-white flex items-center justify-center hover:bg-[#002A5C] disabled:opacity-50 transition-colors"
            >
              <Send size={16} />
            </button>
          </form>
          <div className="text-[10px] text-neutral-500 px-4 py-2 border-t border-neutral-100 bg-white">
            Respostas automáticas por IA · Para casos complexos:{" "}
            <a href={`mailto:${SITE.email}`} className="text-[#003F88] underline">{SITE.email}</a>
          </div>
        </div>
      )}
    </>
  );
}
